import { buildChatsQuery } from './history_language';
import { languaSessionHeader } from '../../services/languaSessionHeader';

class SpeakApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'SpeakApiError';
    this.status = status;
  }
}

export default class SpeakApiClient {
  constructor(authToken) {
    this.authToken = authToken;
  }

  getRecommendation() {
    return this.request('/api/v1/recommendations');
  }

  skipRecommendation(recommendation) {
    return this.request('/api/v1/recommendations/skip_current_recommendation', {
      method: 'POST',
      body: {
        chat_key: recommendation?.chat_key,
        activity_type: recommendation?.activity_type
      }
    });
  }

  startRecommendation(chatSettings = {}) {
    return this.request('/api/v1/recommendations/start_chat', {
      method: 'POST',
      body: chatSettings
    });
  }

  getReviewLastChat() {
    return this.request('/api/v1/chats/review_last_chat');
  }

  getActivityHistories(language) {
    const params = new URLSearchParams({ language, limit: '1000' });
    return this.request(`/api/v1/activity_histories?${params.toString()}`);
  }

  getGrammarRecommendations() {
    return this.request('/api/v1/user_grammar_recommendations');
  }

  getChats(page = 1, query = '', language = '') {
    return this.request(`/api/v1/chats?${buildChatsQuery(page, query, language)}`);
  }

  updateChatTitle(id, title) {
    return this.request(`/api/v1/chats/${encodeURIComponent(id)}/update_title`, {
      method: 'PATCH',
      body: { title }
    });
  }

  deleteChat(id) {
    return this.request(`/api/v1/chats/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  }

  // voiceVersion: the Latest | Standard choice to hear (LAN-1654). The server
  // uses the team's saved one without it.
  getVoiceSample(voice, language, voiceVersion) {
    const versionParam = voiceVersion ? `voice_version=${encodeURIComponent(voiceVersion)}` : '';
    if (voice.sample_url) {
      if (!versionParam) return this.request(voice.sample_url);

      return this.request(`${voice.sample_url}${voice.sample_url.includes('?') ? '&' : '?'}${versionParam}`);
    }

    const params = new URLSearchParams({ language });
    if (voiceVersion) params.set('voice_version', voiceVersion);

    return this.request(`/api/v1/voice_samples/${encodeURIComponent(voice.id)}?${params.toString()}`);
  }

  updateLanguaSettings(payload) {
    return this.request('/api/v1/langua_settings', {
      method: 'PATCH',
      body: { langua_settings: payload }
    });
  }

  async request(path, options = {}) {
    const headers = {
      // Declare the surface: api/v1 is shared with the mobile app, so the
      // server cannot tell us apart from the namespace or the auth (both use
      // Bearer tokens). See LAN-424.
      'X-Langua-Client': 'web',
      'X-Langua-Platform': 'web',
      ...languaSessionHeader(),
      Accept: 'application/json'
    };

    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`;
    }

    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(path, {
      method: options.method || 'GET',
      headers,
      credentials: 'same-origin',
      signal: options.signal,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined
    });

    const text = await response.text();
    let data = {};
    let parseFailed = false;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch (error) {
        parseFailed = true;
      }
    }

    if (!response.ok) {
      const message = (!parseFailed && (data.error || data.errors?.join(', '))) ||
        `${response.status} ${response.statusText}`.trim();
      const error = new SpeakApiError(message, response.status);
      error.details = data;
      error.retryAfter = response.headers?.get('Retry-After');
      throw error;
    }

    if (parseFailed) {
      throw new Error('Unexpected response from server.');
    }

    return data;
  }
}
